import { useMemo, useState } from 'react'
import ReactPhoneInput, { getCountryCallingCode } from 'react-phone-number-input'
import type { Country, Value } from 'react-phone-number-input'
import ptBR from 'react-phone-number-input/locale/pt-BR.json'
import 'react-phone-number-input/style.css'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/** Sem acento e sem caixa, dos dois lados da comparação: quem digita "sao tome"
 *  precisa achar "São Tomé e Príncipe". */
function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/** O DDI do país, ou string vazia para a opção "Internacional" (que não tem um).
 *  `getCountryCallingCode` lança para país fora dos metadados — daí o try. */
function callingCode(country?: Country): string {
  if (!country) return ''
  try {
    return `+${getCountryCallingCode(country)}`
  } catch {
    return ''
  }
}

interface CountryOption {
  value?: Country
  label: string
}

interface CountrySelectProps {
  value?: Country
  options: CountryOption[]
  onChange: (country?: Country) => void
  disabled?: boolean
  readOnly?: boolean
  iconComponent: React.ElementType<{ country?: Country; label: string }>
  'aria-label'?: string
}

/**
 * Seletor de país com busca.
 *
 * Substitui o `<select>` nativo da react-phone-number-input por dois motivos que
 * apareceram no uso real: o nativo não tem campo de busca (só type-ahead por
 * prefixo, que não acha "Coreia do Sul" quem digita "coreia") e não mostra o
 * DDI, então quem só sabe que o número começa em +351 não tinha por onde chegar
 * em Portugal.
 *
 * A busca casa por nome em português, por DDI (com ou sem o "+") e pela sigla do
 * país — é o `value` de cada item que carrega os três, e o filtro abaixo compara
 * sem acento.
 */
function CountrySelect({
  value,
  options,
  onChange,
  disabled,
  readOnly,
  iconComponent: Icon,
  'aria-label': ariaLabel,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false)

  // O DDI de cada país é fixo; recalcular a lista a cada tecla digitada na busca
  // custaria ~250 chamadas por tecla.
  const items = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        code: callingCode(option.value),
        // O que a busca enxerga: "Brasil +55 BR".
        haystack: normalize(
          [option.label, callingCode(option.value), option.value]
            .filter(Boolean)
            .join(' '),
        ),
      })),
    [options],
  )

  const selected = items.find((item) => item.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled || readOnly}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled || readOnly}
          className="mr-2 flex h-9 shrink-0 items-center gap-1 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon country={value} label={selected?.label ?? ''} />
          <span className="tabular-nums text-muted-foreground">
            {selected?.code || '+'}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            // `itemValue` é o haystack que montamos abaixo. O "+" digitado é
            // descartado para "+55" e "55" acharem a mesma coisa.
            const needle = normalize(search).replace(/^\+/, '')
            return itemValue.includes(needle) ? 1 : 0
          }}
        >
          <CommandInput placeholder="País ou DDI (ex.: 55)" />
          <CommandList>
            <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value ?? 'ZZ'}
                  value={item.haystack}
                  onSelect={() => {
                    onChange(item.value)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4 shrink-0',
                      item.value === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <Icon country={item.value} label={item.label} />
                  <span className="ml-2 truncate">{item.label}</span>
                  <span className="ml-auto pl-2 tabular-nums text-muted-foreground">
                    {item.code}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function PhoneInput({ value, onChange, id }: PhoneInputProps) {
  return (
    <div className="phone-input-wrapper">
      <ReactPhoneInput
        id={id}
        international
        defaultCountry="BR"
        // Nomes de país em português. Sem isto a lista vinha em inglês, e país
        // com nome diferente nos dois idiomas ("Coreia do Sul"/"South Korea",
        // "Alemanha"/"Germany") ficava impossível de achar.
        // `pt-BR` e não `pt`: o segundo traz nome de tratado ("Brasil,
        // República Federativa do").
        labels={ptBR}
        countrySelectComponent={CountrySelect}
        value={(value as Value) || ''}
        onChange={(v: Value | undefined) => {
          onChange?.({
            target: { value: v ?? '' },
          } as React.ChangeEvent<HTMLInputElement>)
        }}
        numberInputProps={{
          className:
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        }}
      />
    </div>
  )
}
